import React, { Fragment, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from 'styled-components';
import { isEmpty } from 'lodash-es';
import queryString from 'query-string';
import { BaseBox, CenterBox, Heading2, Heading4 } from '@docon/docon-ui-library';
import { getAppointments } from 'redux/reducer/appointment';
import AppointmentCard from './components/Card';
import Appointment from './modal/details';
import Layout from 'components/Layout';
import PaymentSuccess from 'components/BookAppointment/PaymentSuccess';
import { Loader, Image } from 'shared';
import { colors } from 'utilities/colorConfig';

const appointmentBatchCount = 20;
const Root = styled(Layout)`
  .zeroCase {
    position: relative;
    top: 40%;
    text-align: center;
    transform: translateY(-50%);

    ${CenterBox} {
      img {
        max-width: 70%;
      }
    }

    ${Heading4} {
      color: ${colors.secondaryColor}
    }
  }

  .year {
    font-weight: 600;
    margin-left: 5px;
    margin-bottom: 15px;
  }
  
  ${AppointmentCard} {
    margin-bottom: 12px;
  }
`;

function Appointments({
  history,
}: any) {
  const location = useLocation();
  const {
    source_id,
    source_type,
    payment_status,
    appointmentID,
  } = queryString.parse(location.search);

  const [activeAppointment, setActiveAppointment] = useState<any>({});
  const [openPaymentSuccess, setOpenPaymentSuccess] = useState<any>(false);
  const [pageNo, setPageNo] = useState(0);
  const { user } = useSelector(state => state.auth);
  const {
    isAppointmentCanceled,
    isFetchingAppointments,
    appointments, appointmentsByYear, years,
    hasMoreAppointment,
    closingBookingModal,
  } = useSelector(state => state.appointment);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchAppointments(1);
  }, []);

  useEffect(() => {
    if (payment_status)
      setOpenPaymentSuccess(true);
  }, [payment_status]);

  useEffect(() => {
    if (appointmentID && pageNo === 1) {
      const appointment = appointments.find(appointment => appointment.appointmentID === appointmentID);
      setActiveAppointment(appointment);
    }
  }, [appointments]);

  useEffect(() => {
    if (isAppointmentCanceled || closingBookingModal) {
      setActiveAppointment({});
      fetchAppointments(1);
    }
  }, [isAppointmentCanceled, closingBookingModal]);

  function fetchAppointments(page?: number) {
    setPageNo(page || pageNo + 1);
    dispatch(getAppointments({
      userID: user.userID, params: {
        mobileNumber: user.mobileNumber,
        offset: appointmentBatchCount,
        page: page || pageNo + 1,
        teleConsult: true,
        type: 'appointment',
      },
    }));
  }

  function handleCardClick(appointment) {
    setActiveAppointment(appointment);
  }

  const handleModalClose = (modal: string) => () => {
    switch (modal) {
      case 'paymentSuccess':
        history.replace(location.pathname);
        return setOpenPaymentSuccess(false);
      case 'appointmentDetails':
        return setActiveAppointment({});
      default:
        break;
    }
  };

  if (!isFetchingAppointments && isEmpty(years)) {
    return (
      <Root>
        <BaseBox className='zeroCase'>
          <CenterBox>
            <Image src='/images/empty-states/no-appt@2x.png' alt='' />
          </CenterBox>
          <Heading2 fontWeight={600} m={'16px 0 5px'} title='No Appointments' />
          <Heading4 title='You have no appointments' />
        </BaseBox>
      </Root>
    );
  }

  return (
    <Root>
      {
        !isEmpty(activeAppointment) ? (
          <Appointment
            appointmentId={activeAppointment.appointmentID}
            source={'appointment'}
            parseAppointmentTime={activeAppointment.parseAppointmentTime}
            appointmentDate={activeAppointment.appointmentDate}
            appointmentStatus={activeAppointment.status}
            doctorID={activeAppointment.doctorID}
            clinicID={activeAppointment.clinicID}
            onClose={handleModalClose('appointmentDetails')}
          />
        ) : null
      }
      {
        openPaymentSuccess ? (
          <PaymentSuccess
            isOpen
            paymentID={source_id}
            sourceType={source_type}
            paymentStatus={payment_status}
            onClose={handleModalClose('paymentSuccess')}
          />
        ) : null
      }
      <InfiniteScroll
        loader={<Loader />}
        hasMore={hasMoreAppointment}
        dataLength={appointments.length}
        scrollThreshold={0.85}
        scrollableTarget='mainBody'
        next={fetchAppointments}
      >
        {
          years.map((year: string) => (
            <Fragment key={year}>
              <Heading2 className='year' title={year} />

              {
                appointmentsByYear[year].map((appointment: any) => (
                  <AppointmentCard
                    key={`${appointment.appointmentID}-${appointment.appointmentTime}`}
                    appointment={appointment}
                    onCardClick={handleCardClick}
                  />
                ))
              }
            </Fragment>
          ))
        }
      </InfiniteScroll>
    </Root>
  );
}

export default Appointments;